import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// ============================================
// API FUNCTIONS
// ============================================

const fetchCalendarFilters = async (tenantId) => {
  const response = await api.get('/api/head-of-institution/calendar/filters', {
    headers: { 'x-tenant-id': tenantId }
  });
  return response.data;
};

const fetchCalendarEvents = async (tenantId, filters) => {
  const response = await api.get('/api/head-of-institution/calendar/events', {
    params: filters,
    headers: { 'x-tenant-id': tenantId }
  });
  return response.data;
};

const fetchEventDetails = async (tenantId, eventId, academicYearId) => {
  const response = await api.get('/api/head-of-institution/calendar/event-details', {
    params: { eventId, academicYearId },
    headers: { 'x-tenant-id': tenantId }
  });
  return response.data;
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getEventTypeColor = (type) => {
  const colors = {
    academic: 'border-l-4 border-l-blue-500 border-slate-200 bg-blue-50/40 hover:bg-blue-50/80 transition-colors',
    exam: 'border-l-4 border-l-rose-500 border-slate-200 bg-rose-50/40 hover:bg-rose-50/80 transition-colors',
    holiday: 'border-l-4 border-l-amber-500 border-slate-200 bg-amber-50/40 hover:bg-amber-50/80 transition-colors',
    event: 'border-l-4 border-l-emerald-500 border-slate-200 bg-emerald-50/40 hover:bg-emerald-50/80 transition-colors',
    other: 'border-l-4 border-l-slate-400 border-slate-200 bg-slate-50/60 hover:bg-slate-50 transition-colors'
  };
  return colors[type] || colors.other;
};

const getEventTypeModalColor = (type) => {
  const colors = {
    academic: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-900 border-blue-200',
    exam: 'bg-gradient-to-r from-rose-50 to-rose-100 text-rose-900 border-rose-200',
    holiday: 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-900 border-amber-200',
    event: 'bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-900 border-emerald-200',
    other: 'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-900 border-slate-200'
  };
  return colors[type] || colors.other;
};

const getEventTypeIcon = (type) => {
  const icons = {
    academic: '📚',
    exam: '📝',
    holiday: '🎉',
    event: '🎪',
    other: '📌'
  };
  return icons[type] || icons.other;
};

const getEventTypeBadgeStyle = (type) => {
  const colors = {
    academic: { bg: 'bg-blue-100/80', text: 'text-blue-800', border: 'border-blue-200' },
    exam: { bg: 'bg-rose-100/80', text: 'text-rose-800', border: 'border-rose-200' },
    holiday: { bg: 'bg-amber-100/80', text: 'text-amber-800', border: 'border-amber-200' },
    event: { bg: 'bg-emerald-100/80', text: 'text-emerald-800', border: 'border-emerald-200' },
    other: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' }
  };
  return colors[type] || colors.other;
};

// ============================================
// EVENT TYPE BADGE
// ============================================
const EventTypeBadge = ({ type }) => {
  const style = getEventTypeBadgeStyle(type);
  return (
    <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider border ${style.bg} ${style.text} ${style.border}`}>
      {type || 'other'}
    </span>
  );
};

// ============================================
// SEMESTER TOGGLE COMPONENT - Single Toggle
// ============================================
const SemesterToggle = ({ semesters, selectedSemesterId, onSelect, isLoading }) => {
  // Find odd and even semesters
  const oddSemester = semesters?.find(s => s.semesterType === 'odd');
  const evenSemester = semesters?.find(s => s.semesterType === 'even');
  
  // Determine which is currently selected
  const isOddSelected = selectedSemesterId === oddSemester?._id;
  const isEvenSelected = selectedSemesterId === evenSemester?._id;
  const isSelected = isOddSelected || isEvenSelected;

  // Handle toggle click
  const handleToggle = () => {
    if (isLoading) return;
    
    if (!isSelected) {
      // If nothing selected, select odd by default
      if (oddSemester) {
        onSelect(oddSemester._id);
      }
      return;
    }
    
    // Toggle between odd and even
    if (isOddSelected && evenSemester) {
      onSelect(evenSemester._id);
    } else if (isEvenSelected && oddSemester) {
      onSelect(oddSemester._id);
    }
  };

  if (!oddSemester && !evenSemester) {
    return (
      <div className="text-xs text-slate-400 italic">No semesters available</div>
    );
  }

  // Get selected semester for display
  const selectedSemester = semesters?.find(s => s._id === selectedSemesterId);
  const isOdd = selectedSemester?.semesterType === 'odd';

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-3">
        {/* Toggle Switch */}
        <button
          onClick={handleToggle}
          disabled={isLoading || !oddSemester || !evenSemester}
          className={`
            relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300
            ${!isSelected ? 'bg-slate-300' : isOdd ? 'bg-blue-600' : 'bg-emerald-600'}
            ${(isLoading || !oddSemester || !evenSemester) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            shadow-inner
          `}
        >
          <span
            className={`
              inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300
              ${!isSelected ? 'translate-x-1' : isOdd ? 'translate-x-9' : 'translate-x-9'}
            `}
          />
        </button>

        {/* Current Selection Label */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">
            {!isSelected ? (
              'Select Semester'
            ) : isOdd ? (
              <span className="flex items-center gap-1.5 text-blue-700">
                <span>📘</span> Odd Semester
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-emerald-700">
                <span>📗</span> Even Semester
              </span>
            )}
          </span>
          {isSelected && (
            <button
              onClick={() => onSelect(null)}
              className="text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-1 transition-colors"
              title="Clear selection"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Show selected semester dates */}
      {selectedSemester && (
        <div className="text-[10px] text-slate-400 flex items-center gap-1.5 ml-1">
          <span className="inline-block w-1 h-1 rounded-full bg-slate-300"></span>
          <span>
            {new Date(selectedSemester.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {' - '}
            {new Date(selectedSemester.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      )}
    </div>
  );
};

// ============================================
// EVENT DETAILS MODAL
// ============================================
const EventDetailsModal = ({ event, onClose, isLoading }) => {
  if (!event) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md" onClick={onClose}>
      <div 
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col transform transition-all animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-slate-700 hover:text-slate-900 transition-colors bg-white/80 hover:bg-white rounded-full p-2 border border-slate-200 shadow-sm"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {isLoading ? (
          <div className="p-12 flex flex-col items-center justify-center space-y-4">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-blue-600 border-t-transparent" />
            <p className="text-xs font-medium text-slate-500">Fetching event details...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className={`p-6 pr-14 border-b ${getEventTypeModalColor(event.eventType)}`}>
              <div className="flex items-start gap-3.5">
                <span className="text-3xl p-2 bg-white/60 rounded-xl shadow-inner shrink-0" role="img" aria-label="event icon">
                  {getEventTypeIcon(event.eventType)}
                </span>
                <div className="space-y-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 leading-snug break-words">{event.eventName}</h3>
                  <div className="flex flex-wrap gap-2">
                    <EventTypeBadge type={event.eventType || 'other'} />
                    {event.semesterType && (
                      <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider border ${
                        event.semesterType === 'odd' 
                          ? 'bg-blue-100 text-blue-800 border-blue-200' 
                          : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      }`}>
                        {event.semesterType === 'odd' ? '📘 Odd Semester' : '📗 Even Semester'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto">
              {/* Date Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Start Date</p>
                  <p className="text-xs font-semibold text-slate-800 mt-1">
                    {event.startDate ? new Date(event.startDate).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 'N/A'}
                  </p>
                </div>
                {event.endDate && event.endDate !== event.startDate ? (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">End Date</p>
                    <p className="text-xs font-semibold text-slate-800 mt-1">
                      {new Date(event.endDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">End Date</p>
                    <p className="text-xs font-medium text-slate-400 mt-1">Same Day Event</p>
                  </div>
                )}
              </div>

              {/* Time Configuration */}
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Timing</p>
                  <p className="text-xs font-semibold text-slate-800 mt-1">
                    {event.isAllDay ? 'Full Day Duration' : `${event.startTime || 'N/A'} ${event.endTime ? ` - ${event.endTime}` : ''}`}
                  </p>
                </div>
                {event.isAllDay && (
                  <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                    🟢 All Day
                  </span>
                )}
              </div>

              {/* Location */}
              {event.location && (
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Location</p>
                  <p className="text-xs font-semibold text-slate-800 mt-1 flex items-center gap-1.5">
                    <span className="text-slate-400">📍</span> {event.location}
                  </p>
                </div>
              )}

              {/* Description */}
              {event.description && (
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/60">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Description</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{event.description}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// LOADING COMPONENT
// ============================================
const CalendarLoading = () => (
  <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
    <div className="border-b border-slate-200 bg-slate-900 px-6 py-5">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-white/10 animate-pulse" />
        <div className="space-y-2 flex-1">
          <div className="h-4 w-36 rounded bg-white/20 animate-pulse" />
          <div className="h-3 w-56 rounded bg-white/10 animate-pulse" />
        </div>
      </div>
    </div>
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-24 rounded bg-slate-200 animate-pulse" />
            <div className="h-11 rounded-xl bg-slate-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const Calendar = () => {
  const navigate = useNavigate();
  
  // State variables
  const [tenantId, setTenantId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [semesterId, setSemesterId] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
  const [monthWiseEvents, setMonthWiseEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingModalDetail, setLoadingModalDetail] = useState(false);

  // Load tenantId securely from localStorage
  useEffect(() => {
    const tid = localStorage.getItem('tenantId');
    if (tid) setTenantId(tid);
  }, []);

  // Fetch initial system filter capabilities
  useEffect(() => {
    if (!tenantId) return;
    
    fetchCalendarFilters(tenantId)
      .then(response => {
        if (response.success) {
          setAcademicYears(response.data.academicYears || []);
        }
      })
      .catch(() => setError('Failed to retrieve structured calendar setup filters.'));
  }, [tenantId]);

  // Core stable retrieval dynamic memoized method
  const fetchEvents = useCallback(async (ayId, semId) => {
    const yearId = ayId || academicYearId;
    const sem = semId || semesterId;
    
    if (!yearId) return;

    const selectedAY = academicYears.find(y => y._id === yearId);
    if (!selectedAY) return;

    // For semester-based, require semester selection
    if (selectedAY.isSemesterBased && !sem) {
      setMonthWiseEvents([]);
      setTodayEvents([]);
      setSummary({});
      return;
    }

    const filters = { academicYearId: yearId };
    if (selectedAY.isSemesterBased && sem) {
      filters.semesterId = sem;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetchCalendarEvents(tenantId, filters);
      if (response.success) {
        setMonthWiseEvents(response.data.monthWiseEvents || []);
        setTodayEvents(response.data.todayEvents || []);
        setSummary(response.data.summary || {});
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error occurred while loading calendar configuration metrics.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, academicYearId, semesterId, academicYears]);

  // Handle academic year change
  const handleAcademicYearChange = (value) => {
    setAcademicYearId(value);
    setSemesterId('');
    setMonthWiseEvents([]);
    setTodayEvents([]);
    setSummary({});
    
    const ay = academicYears.find(y => y._id === value);
    setSelectedAcademicYear(ay);
    
    // For term-based, fetch immediately
    if (ay && !ay.isSemesterBased) {
      fetchEvents(value, '');
    }
  };

  // Handle semester toggle selection
  const handleSemesterSelect = (semId) => {
    setSemesterId(semId);
    // If clearing selection, clear events
    if (!semId) {
      setMonthWiseEvents([]);
      setTodayEvents([]);
      setSummary({});
      return;
    }
    // Fetch events for selected semester
    if (academicYearId && semId) {
      fetchEvents(academicYearId, semId);
    }
  };

  // Auto-fetch when semester changes
  useEffect(() => {
    if (!academicYearId) return;
    
    const selectedAY = academicYears.find(y => y._id === academicYearId);
    if (selectedAY?.isSemesterBased && semesterId) {
      fetchEvents(academicYearId, semesterId);
    }
  }, [semesterId, academicYearId, academicYears, fetchEvents]);

  // Interactive Click Handlers
  const handleEventClick = async (event) => {
    setIsModalOpen(true);
    setLoadingModalDetail(true);
    try {
      const response = await fetchEventDetails(tenantId, event.id, academicYearId);
      if (response.success) {
        setSelectedEvent(response.data.event);
      }
    } catch (err) {
      console.error('Error fetching event details:', err);
      setIsModalOpen(false);
    } finally {
      setLoadingModalDetail(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setLoadingModalDetail(false);
  };

  const handleBack = () => {
    navigate('/head-of-institution/dashboard');
  };

  if (!tenantId) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <CalendarLoading />
      </div>
    );
  }

  const hasSelection = academicYearId && (selectedAcademicYear?.isSemesterBased ? semesterId : true);

  return (
    <div className="p-4 md:p-8 bg-slate-50 min-h-screen font-sans text-slate-900 antialiased selection:bg-blue-500 selection:text-white">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Back Navigation Bar Control */}
        <div>
          <button
            onClick={handleBack}
            className="group flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-semibold text-slate-600 shadow-sm hover:text-slate-900 transition-all border border-slate-200 hover:shadow-md active:scale-[0.98]"
          >
            <svg className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 transition-colors transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
        </div>

        {/* Branding Hero Banner Headliner */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-slate-200 pb-5">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900 md:text-3xl flex items-center gap-2">
              <span>📅</span> Academic Calendar
            </h1>
            <p className="text-xs md:text-sm font-medium text-slate-500 mt-1">
              Oversee schedules, institutional terms, events, and examination windows.
            </p>
          </div>
        </div>

        {/* Filter Setting Layout Section */}
        <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-slate-900 border-b border-slate-800 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-bold text-white tracking-wide">Select Academic Context</h2>
                <p className="text-[11px] font-medium text-slate-400 mt-0.5">Isolate institutional tracking matrixes configurations</p>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {error && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 shadow-inner">
                <svg className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xs font-semibold text-amber-900">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              {/* Year Select input selector container field */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Academic Year <span className="text-rose-500">*</span>
                </label>
                <select
                  value={academicYearId}
                  onChange={(e) => handleAcademicYearChange(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2364748B%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:10px_auto] bg-[right_14px_center] bg-no-repeat"
                >
                  <option value="">Choose active year...</option>
                  {academicYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {ay.name} {ay.status === 'active' ? '— (Current Active)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Semester Toggle - Single toggle for Odd/Even */}
              {selectedAcademicYear?.isSemesterBased && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Semester Selection
                  </label>
                  <SemesterToggle
                    semesters={selectedAcademicYear.semesters}
                    selectedSemesterId={semesterId}
                    onSelect={handleSemesterSelect}
                    isLoading={loading}
                  />
                </div>
              )}

              {/* Term Base fallbacks metrics details summary indicator */}
              {selectedAcademicYear && !selectedAcademicYear.isSemesterBased && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Academic Period Mode
                  </label>
                  <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-2 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-inner">
                    <span>✨ Annual Term View Enabled</span>
                    <span className="bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded-md text-[10px] uppercase font-bold tracking-wider">
                      {selectedAcademicYear.terms?.length || 0} Blocks Available
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Structured Stats Section Dashboard widgets elements */}
            {hasSelection && !loading && (summary.total > 0 || monthWiseEvents.length > 0) && (
              <div className="mt-5 border-t border-slate-100 pt-5 animate-in fade-in duration-300">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total</p>
                    <p className="text-xl font-extrabold text-slate-800 mt-0.5">{summary.total || 0}</p>
                  </div>
                  <div className="bg-blue-50/60 rounded-xl p-3 text-center border border-blue-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-blue-500">📚 Academic</p>
                    <p className="text-xl font-extrabold text-blue-800 mt-0.5">{summary.academic || 0}</p>
                  </div>
                  <div className="bg-rose-50/60 rounded-xl p-3 text-center border border-rose-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-rose-500">📝 Exam</p>
                    <p className="text-xl font-extrabold text-rose-800 mt-0.5">{summary.exam || 0}</p>
                  </div>
                  <div className="bg-amber-50/60 rounded-xl p-3 text-center border border-amber-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">🎉 Holiday</p>
                    <p className="text-xl font-extrabold text-amber-800 mt-0.5">{summary.holiday || 0}</p>
                  </div>
                  <div className="bg-emerald-50/60 rounded-xl p-3 text-center border border-emerald-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">🎪 Events</p>
                    <p className="text-xl font-extrabold text-emerald-800 mt-0.5">{summary.event || 0}</p>
                  </div>
                  <div className="bg-purple-50/60 rounded-xl p-3 text-center border border-purple-200/60 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600">📌 Today</p>
                    <p className="text-xl font-extrabold text-purple-800 mt-0.5">{summary.today || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* TODAY'S EVENTS (WITH NO EVENTS FALLBACK) */}
        {/* ============================================ */}
        {hasSelection && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-4">
              <span className="text-lg">📌</span>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Today's Live Activities</h3>
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${todayEvents.length > 0 ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                {todayEvents.length} Active
              </span>
            </div>

            {todayEvents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {todayEvents.map((event, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleEventClick(event)}
                    className={`rounded-xl p-4 flex flex-col justify-between gap-3 cursor-pointer border ${getEventTypeColor(event.eventType)} shadow-sm hover:shadow-md transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <span className="text-xl p-1 bg-white/70 rounded-lg shadow-sm shrink-0 mt-0.5" role="img" aria-label="type icon">
                          {getEventTypeIcon(event.eventType)}
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate leading-snug" title={event.eventName}>
                            {event.eventName}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <p className="text-[11px] text-slate-400 flex items-center gap-1">
                              <span>📅</span> {event.startDate ? new Date(event.startDate).toLocaleDateString() : 'N/A'}
                            </p>
                            {event.semesterType && (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                event.semesterType === 'odd' 
                                  ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              }`}>
                                {event.semesterType === 'odd' ? 'Odd' : 'Even'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0">
                        <EventTypeBadge type={event.eventType || 'other'} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center flex flex-col items-center justify-center space-y-2">
                <span className="text-2xl text-slate-300">🍃</span>
                <p className="text-xs font-bold text-slate-500">No Events Scheduled Today</p>
                <p className="text-[10px] text-slate-400 max-w-sm">
                  There are no administrative timelines, public holidays, or exams cataloged for today's date context.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* MONTH-WISE CALENDAR VIEW */}
        {/* ============================================ */}
        {hasSelection && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <span>📅</span> Complete Schedule Calendar Breakdown
                {loading && <span className="ml-2 animate-pulse text-[11px] font-normal text-blue-500 lowercase italic">Syncing view structural elements...</span>}
              </h3>
              {!loading && monthWiseEvents.length > 0 && (
                <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-bold text-slate-600 border border-slate-300">
                  {summary.months || 0} Months Distributed
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 bg-white border border-slate-200 rounded-2xl space-y-3 shadow-sm">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                <p className="text-xs font-medium text-slate-400">Loading catalog items, please wait...</p>
              </div>
            ) : monthWiseEvents.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
                <p className="text-sm font-bold text-slate-400">Empty Calendar Record Scope</p>
                <p className="text-xs text-slate-400 mt-1">No historical or tentative event markers map into this parameters matrix configuration.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {monthWiseEvents.map((monthData) => (
                  <div key={monthData.monthKey} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    
                    {/* Month Header Banner Context Title */}
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
                      <h4 className="text-xs font-extrabold text-slate-700 tracking-wide uppercase">
                        {monthData.display}
                      </h4>
                      <span className="text-[10px] font-bold bg-white px-2 py-0.5 rounded-md text-slate-500 shadow-sm border border-slate-200">
                        {monthData.events.length} {monthData.events.length === 1 ? 'Event' : 'Events'}
                      </span>
                    </div>
                    
                    {/* Responsive Events Grid Tracks adaptive */}
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {monthData.events.map((event, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleEventClick(event)}
                          className={`rounded-xl p-3.5 border flex flex-col justify-between gap-3 shadow-sm ${getEventTypeColor(event.eventType)} cursor-pointer transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200`}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-base p-0.5 bg-white/70 rounded shadow-sm shrink-0" role="img" aria-label="type stamp">
                                  {getEventTypeIcon(event.eventType)}
                                </span>
                                <p className="text-xs font-bold text-slate-800 truncate" title={event.eventName}>
                                  {event.eventName}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium">
                              <span className="flex items-center gap-1 text-slate-400">📅 {event.startDate ? new Date(event.startDate).toLocaleDateString() : 'N/A'}</span>
                              {event.semesterType && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                  event.semesterType === 'odd' 
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                    : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                }`}>
                                  {event.semesterType === 'odd' ? 'Odd Sem' : 'Even Sem'}
                                </span>
                              )}
                              {event.location && (
                                <span className="flex items-center gap-1 text-slate-400 truncate max-w-[150px]">
                                  📍 {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-end border-t border-slate-200/40 pt-2 mt-1">
                            <EventTypeBadge type={event.eventType || 'other'} />
                          </div>
                        </div>
                      ))}
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Dynamic Deep Detail Overlay Drawer Modal Layer */}
      {isModalOpen && (
        <EventDetailsModal 
          event={selectedEvent} 
          onClose={handleCloseModal} 
          isLoading={loadingModalDetail}
        />
      )}
    </div>
  );
};

export default Calendar;